import { useContext, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { QUERY_FETCH_SCHEDULE_OPC } from "services/graphQL/queries";
import {
  getOpcSessionImportFailures,
  onOpcSessionImportFailuresChange
} from "services/opc-import-failure-state";
import {
  isScheduleImportFailed,
  TScheduleSourceInfo
} from "./schedules-in-opc-import-state";

function isSchedulesSubTabPath(pathname: string, projectId?: string): boolean {
  if (!projectId) return false;
  const schedulesPath = `/project/${projectId}/schedule/project-schedules`;
  const opcPath = `${schedulesPath}/opc`;
  return Boolean(
    matchPath(pathname, { path: schedulesPath, exact: true }) ||
    matchPath(pathname, { path: opcPath, exact: true })
  );
}

/**
 * True when any OPC schedule in this project has a failed import — either from
 * the newest import_log on the list, or from a failure this session that has
 * not yet been reflected in that list. Drives the red dot on the Schedules in
 * OPC sub-tab.
 *
 * The list query only runs on the Schedules / Schedules in OPC routes — the only
 * places the sub-tab (and its dot) are shown — so Change Impact and the linking
 * pages do not pay for an extra OPC fetch. On the OPC page itself Apollo shares
 * the cache with SchedulesInOpc's identical query.
 */
export default function useHasOpcImportFailure(): boolean {
  const { projectId } = useParams<{ projectId: string }>();
  const { pathname } = useLocation();
  const { gqlClientForProject, isFeatureFlagEnabled }: TProjectContext =
    useContext(ProjectContext);

  const isOpcIntegrationEnabled = isFeatureFlagEnabled("OPC_INTEGRATION_V1");
  const isOnSchedulesSubTabs = isSchedulesSubTabPath(pathname, projectId);

  const { data } = useCIQQuery<{
    schedule_source_info: Array<TScheduleSourceInfo>;
  }>(QUERY_FETCH_SCHEDULE_OPC, {
    client: gqlClientForProject,
    skip:
      !gqlClientForProject || !isOpcIntegrationEnabled || !isOnSchedulesSubTabs
  });

  const [sessionFailedIds, setSessionFailedIds] = useState<ReadonlySet<string>>(
    () => getOpcSessionImportFailures(projectId)
  );

  useEffect(() => {
    setSessionFailedIds(getOpcSessionImportFailures(projectId));
    return onOpcSessionImportFailuresChange(() => {
      setSessionFailedIds(getOpcSessionImportFailures(projectId));
    });
  }, [projectId]);

  return useMemo(() => {
    if (!isOnSchedulesSubTabs) return false;
    const hasServerFailure = (data?.schedule_source_info ?? []).some(
      isScheduleImportFailed
    );
    return hasServerFailure || sessionFailedIds.size > 0;
  }, [data, sessionFailedIds, isOnSchedulesSubTabs]);
}
